import './settings/settings-leaf.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, Checkbox, Select, TextArea, TextInput, classes } from './ui.tsx';
import {
  DEFAULT_WIDGET_SETTINGS,
  WIDGET_BUTTON_LABEL_MAX,
  WIDGET_CORNERS,
  WIDGET_FIELD_SETS,
  WIDGET_HEADLINE_MAX,
  WIDGET_ICONS,
  WIDGET_INTRO_MAX,
  WIDGET_POSITIONS,
  WIDGET_THEMES,
  accentForeground,
  isWidgetAccentColor,
  type WidgetIcon,
  type WidgetSettings,
} from '@shared/widget.ts';

/**
 * The public quote-request form and its embeddable widget, managed together:
 * the link (create / rotate / switch off), the style options that skin the
 * hosted form, the embedded form and the floating launcher alike, a live
 * preview fed by the unsaved edits, and the copy-paste embed code.
 *
 * Unlike share links the form's URL is re-displayable — it is public by
 * design, meant to be linked from the company's own website — so the section
 * always shows the current address. Creating again is deliberately a rotation
 * (the old address dies), which is the remedy for a URL attracting junk.
 */

/** The same glyphs the loader script draws, for the launcher preview. */
const ICON_PATHS: Record<WidgetIcon, string> = {
  sparkle: 'M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z',
  chat: 'M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H9l-5 4V5a1 1 0 0 1 1-1z',
  broom: 'M20.7 4.7l-1.4-1.4-6.4 6.4 1.4 1.4zM12 11l1 1c-.4 4.4-4.4 8-9 8 .4-4.6 3.8-8.6 8-9z',
  calendar:
    'M7 2h2v3H7zM15 2h2v3h-2zM4 6h16a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1zm1 5v8h14v-8z',
};

function IconGlyph({ icon }: { icon: WidgetIcon }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d={ICON_PATHS[icon]} />
    </svg>
  );
}

/** Query-string overrides so the preview iframe shows unsaved edits. */
function previewQuery(settings: WidgetSettings): string {
  const params = new URLSearchParams();
  params.set('accent', settings.accentColor);
  params.set('theme', settings.theme);
  params.set('corners', settings.corners);
  params.set('fields', settings.fieldSet);
  params.set('logo', settings.showLogo ? '1' : '0');
  // Always present: an empty value is how the preview says "cleared" — the
  // server reads present-but-empty as "use the built-in wording", so wiping a
  // saved headline previews the standard one rather than the stale save.
  params.set('headline', settings.headline ?? '');
  params.set('intro', settings.intro ?? '');
  return params.toString();
}

export function WidgetSection() {
  const { t } = useTranslation();
  const { company, refresh } = useSession();
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [settings, setSettings] = useState<WidgetSettings>(() => company?.widgetSettings ?? DEFAULT_WIDGET_SETTINGS);
  // Free-typed hex, kept apart from the settings so a half-typed color does
  // not thrash the preview or get silently "corrected" mid-keystroke.
  const [accentText, setAccentText] = useState(settings.accentColor);

  // The session query owns the canonical company record; mirror it locally so
  // the style form is editable without optimistic writes. Guarded the same
  // way the company form guards its own draft: the session refreshes whenever
  // ANY section on the page saves (the company form, a logo upload, the link
  // buttons here), and only a clean style draft — or the refresh our own save
  // asked for — should be replaced by what comes back.
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const lastAdopted = useRef(company?.widgetSettings ?? null);
  /**
   * The exact draft object the in-flight save sent. Adoption after our own
   * save is conditional on the draft still BEING that object: the controls
   * stay editable while the request runs, and an edit made mid-flight must
   * survive the refresh (still dirty, still saveable) rather than being
   * replaced by the older snapshot the server just echoed back.
   */
  const submitted = useRef<WidgetSettings | null>(null);
  useEffect(() => {
    if (!company) return;
    const cleanOrJustSaved =
      settingsRef.current === lastAdopted.current ||
      lastAdopted.current === null ||
      (submitted.current !== null && settingsRef.current === submitted.current);
    if (cleanOrJustSaved) {
      setSettings(company.widgetSettings);
      setAccentText(company.widgetSettings.accentColor);
    }
    submitted.current = null;
    lastAdopted.current = company.widgetSettings;
  }, [company]);

  const linkHandlers = {
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError: (caught: unknown) => setError(caught instanceof ApiRequestError ? caught.message : t('widget.error')),
  };

  const create = useMutation({ mutationFn: () => companyApi.createIntakeLink(), ...linkHandlers });
  const disable = useMutation({ mutationFn: () => companyApi.disableIntakeLink(), ...linkHandlers });

  const save = useMutation({
    mutationFn: () => companyApi.update({ widgetSettings: settings }),
    onMutate: () => {
      // What this save is actually writing; the adoption guard compares the
      // draft against it when the refresh lands.
      submitted.current = settingsRef.current;
    },
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await refresh();
    },
    onError: (caught) => {
      setSaved(false);
      // A failed save wrote nothing; the draft is still unsaved work and no
      // later refresh may treat it as "just saved".
      submitted.current = null;
      setError(caught instanceof ApiRequestError ? caught.message : t('widget.saveError'));
    },
  });

  function set<K extends keyof WidgetSettings>(key: K, value: WidgetSettings[K]) {
    setSettings((current) => ({ ...current, [key]: value }));
    setSaved(false);
  }

  const intakePath = company?.intakePath ?? null;
  const origin = window.location.origin;
  const url = intakePath ? `${origin}${intakePath}` : null;
  const token = intakePath ? intakePath.slice('/r/'.length) : null;

  // The preview reloads its iframe on every settings change; debounced so
  // typing a headline does not refetch the form letter by letter.
  const liveQuery = useMemo(() => previewQuery(settings), [settings]);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!intakePath) {
      setPreviewSrc(null);
      return;
    }
    // preview=1 renders the form inert: an owner poking their own preview
    // must not be able to file a real request into their own inbox.
    const timer = setTimeout(() => setPreviewSrc(`${intakePath}/embed?${liveQuery}&preview=1`), 400);
    return () => clearTimeout(timer);
  }, [intakePath, liveQuery]);

  async function copy(kind: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setError(t('widget.copyError'));
    }
  }

  // data-mode is pinned in the snippet rather than left to widget.json: the
  // snippet's shape follows the (possibly unsaved) mode picked above, and a
  // pasted inline-looking snippet must never turn into a floating launcher
  // because the stored mode still said so.
  const scriptSnippet =
    token === null
      ? ''
      : settings.mode === 'inline'
        ? `<div id="cleaning-quote-form"></div>\n<script async src="${origin}/r/embed/v1.js" data-cqb="${token}" data-mode="inline" data-target="#cleaning-quote-form"></script>`
        : `<script async src="${origin}/r/embed/v1.js" data-cqb="${token}" data-mode="float"></script>`;

  const iframeSnippet =
    intakePath === null
      ? ''
      : `<iframe src="${origin}${intakePath}/embed" style="width:100%;border:0;min-height:640px" title="${t('widget.iframeTitle')}" loading="lazy"></iframe>`;

  const launcherForeground = accentForeground(settings.accentColor);
  const launcherRadius = settings.corners === 'sharp' ? '0' : settings.corners === 'pill' ? '999px' : '14px';

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="widget-heading">
      <div className="work-section-heading">
        <h2 id="widget-heading" className="mb-2 text-lg font-semibold">
          {t('widget.heading')}
        </h2>
      </div>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('widget.description')}</p>

      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {url ? (
        <div className="mb-3">
          <p className="field-label">{t('widget.linkLabel')}</p>
          <p className="break-all font-mono text-sm">{url}</p>
          <p className="mt-1 text-sm">
            <Link to="/requests" className="text-[color:var(--color-brand)] hover:underline">
              {t('widget.viewRequests')}
            </Link>
          </p>
        </div>
      ) : (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('widget.off')}</p>
      )}

      <div className="flex flex-wrap gap-2">
        {url ? (
          <>
            <Button onClick={() => void copy('link', url)}>
              {copied === 'link' ? t('common.copied') : t('common.copyLink')}
            </Button>
            <Button
              busy={create.isPending}
              onClick={() => {
                if (window.confirm(t('widget.confirmRotate'))) create.mutate();
              }}
            >
              {t('widget.rotate')}
            </Button>
            <Button
              variant="danger"
              busy={disable.isPending}
              onClick={() => {
                if (window.confirm(t('widget.confirmDisable'))) disable.mutate();
              }}
            >
              {t('widget.disable')}
            </Button>
          </>
        ) : (
          <Button variant="primary" busy={create.isPending} onClick={() => create.mutate()}>
            {t('widget.enable')}
          </Button>
        )}
      </div>

      {url && token ? (
        <>
          {/* ---- Style options ---------------------------------------- */}
          <h3 className="mt-6 mb-1 border-t border-[color:var(--color-line)] pt-6 text-base font-semibold">
            {t('widget.styleHeading')}
          </h3>
          <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('widget.styleDescription')}</p>

          {/* Mode: what the embed code produces on the company's site. */}
          <fieldset className="mb-4">
            <legend className="field-label">{t('widget.mode')}</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {(['float', 'inline'] as const).map((mode) => (
                <label
                  key={mode}
                  className={classes(
                    'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                    settings.mode === mode
                      ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
                      : 'border-[color:var(--color-line)]',
                  )}
                >
                  <input
                    type="radio"
                    name="widget-mode"
                    className="mt-1"
                    checked={settings.mode === mode}
                    onChange={() => set('mode', mode)}
                  />
                  <span>
                    <span className="block font-semibold">{t(`widget.modeChoice.${mode}`)}</span>
                    <span className="field-hint">{t(`widget.modeChoiceHint.${mode}`)}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-x-4 sm:grid-cols-2">
            {settings.mode === 'float' ? (
              <Select
                label={t('widget.position')}
                value={settings.position}
                options={WIDGET_POSITIONS.map((position) => ({
                  value: position,
                  label: t(`widget.positionChoice.${position}`),
                }))}
                onChange={(event) => set('position', event.target.value as WidgetSettings['position'])}
              />
            ) : null}
            <Select
              label={t('widget.theme')}
              value={settings.theme}
              options={WIDGET_THEMES.map((theme) => ({ value: theme, label: t(`widget.themeChoice.${theme}`) }))}
              onChange={(event) => set('theme', event.target.value as WidgetSettings['theme'])}
            />
            <Select
              label={t('widget.corners')}
              value={settings.corners}
              options={WIDGET_CORNERS.map((corners) => ({
                value: corners,
                label: t(`widget.cornersChoice.${corners}`),
              }))}
              onChange={(event) => set('corners', event.target.value as WidgetSettings['corners'])}
            />
            <Select
              label={t('widget.fields')}
              hint={t('widget.fieldsHint')}
              value={settings.fieldSet}
              options={WIDGET_FIELD_SETS.map((fieldSet) => ({
                value: fieldSet,
                label: t(`widget.fieldsChoice.${fieldSet}`),
              }))}
              onChange={(event) => set('fieldSet', event.target.value as WidgetSettings['fieldSet'])}
            />
          </div>

          {/* Accent color: a picker and a hex box, kept in step. */}
          <div className="mb-4">
            <label className="field-label" htmlFor="widget-accent">
              {t('widget.accent')}
            </label>
            <div className="flex items-center gap-2">
              <input
                id="widget-accent"
                type="color"
                value={settings.accentColor}
                className="h-11 w-14 cursor-pointer rounded border border-[color:var(--color-line)]"
                onChange={(event) => {
                  set('accentColor', event.target.value);
                  setAccentText(event.target.value);
                }}
              />
              <input
                type="text"
                aria-label={t('widget.accentHex')}
                className="field-control max-w-32"
                value={accentText}
                onChange={(event) => {
                  const value = event.target.value.trim();
                  setAccentText(value);
                  if (isWidgetAccentColor(value)) set('accentColor', value.toLowerCase());
                }}
              />
            </div>
            <p className="field-hint">{t('widget.accentHint')}</p>
          </div>

          {settings.mode === 'float' ? (
            <>
              {/* Launcher icon: pick by looking, not by name. */}
              <fieldset className="mb-4">
                <legend className="field-label">{t('widget.icon')}</legend>
                <div className="flex gap-2">
                  {WIDGET_ICONS.map((icon) => (
                    <label
                      key={icon}
                      className={classes(
                        'flex h-11 w-11 cursor-pointer items-center justify-center rounded-md border',
                        settings.icon === icon
                          ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand)]'
                          : 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
                      )}
                      title={t(`widget.iconChoice.${icon}`)}
                    >
                      <input
                        type="radio"
                        name="widget-icon"
                        className="sr-only"
                        checked={settings.icon === icon}
                        aria-label={t(`widget.iconChoice.${icon}`)}
                        onChange={() => set('icon', icon)}
                      />
                      <IconGlyph icon={icon} />
                    </label>
                  ))}
                </div>
              </fieldset>

              <TextInput
                label={t('widget.buttonLabel')}
                hint={t('widget.buttonLabelHint')}
                maxLength={WIDGET_BUTTON_LABEL_MAX}
                placeholder={t('widget.buttonLabelPlaceholder')}
                value={settings.buttonLabel ?? ''}
                onChange={(event) => set('buttonLabel', event.target.value.length > 0 ? event.target.value : null)}
              />
            </>
          ) : null}

          <div className="grid gap-x-4 sm:grid-cols-2">
            <TextInput
              label={t('widget.headline')}
              maxLength={WIDGET_HEADLINE_MAX}
              placeholder={t('widget.headlinePlaceholder')}
              hint={t('widget.blankForStandard')}
              value={settings.headline ?? ''}
              onChange={(event) => set('headline', event.target.value.length > 0 ? event.target.value : null)}
            />
            <TextArea
              label={t('widget.intro')}
              rows={2}
              maxLength={WIDGET_INTRO_MAX}
              hint={t('widget.blankForStandard')}
              value={settings.intro ?? ''}
              onChange={(event) => set('intro', event.target.value.length > 0 ? event.target.value : null)}
            />
          </div>

          <Checkbox
            label={t('widget.showLogo')}
            hint={t('widget.showLogoHint')}
            checked={settings.showLogo}
            onChange={(event) => set('showLogo', event.target.checked)}
          />

          <div className="mt-2 mb-6 flex flex-wrap items-center gap-3">
            <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
              {t('widget.save')}
            </Button>
            {saved ? (
              <span className="text-sm text-[color:var(--color-brand)]" role="status">
                {t('widget.saved')}
              </span>
            ) : null}
          </div>

          {/* ---- Live preview ----------------------------------------- */}
          <h3 className="mb-1 border-t border-[color:var(--color-line)] pt-6 text-base font-semibold">
            {t('widget.previewHeading')}
          </h3>
          <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('widget.previewNote')}</p>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
            <div className="overflow-hidden rounded-lg border border-[color:var(--color-line)]">
              {previewSrc ? (
                <iframe
                  key={previewSrc}
                  src={previewSrc}
                  title={t('widget.previewFormLabel')}
                  className="h-[480px] w-full"
                />
              ) : null}
            </div>
            {settings.mode === 'float' ? (
              // A mock of the launcher, floating in a miniature "website".
              <div
                aria-hidden="true"
                className="relative h-[180px] rounded-lg border border-dashed border-[color:var(--color-line)] bg-[color:var(--color-surface)]"
              >
                <span className="absolute top-3 left-3 text-xs text-[color:var(--color-muted)]">
                  {t('widget.previewButtonLabel')}
                </span>
                <span
                  className="absolute bottom-4 inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold shadow-lg"
                  style={{
                    background: settings.accentColor,
                    color: launcherForeground,
                    borderRadius: launcherRadius,
                    [settings.position === 'bottom-left' ? 'left' : 'right']: '16px',
                  }}
                >
                  <IconGlyph icon={settings.icon} />
                  {settings.buttonLabel ?? t('widget.buttonLabelPlaceholder')}
                </span>
              </div>
            ) : null}
          </div>

          {/* ---- Embed code ------------------------------------------- */}
          <h3 className="mt-6 mb-1 border-t border-[color:var(--color-line)] pt-6 text-base font-semibold">
            {t('widget.embedHeading')}
          </h3>
          <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('widget.embedDescription')}</p>

          <div className="mb-4">
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="field-label mb-0">{t('widget.embedJsTitle')}</p>
              <Button onClick={() => void copy('script', scriptSnippet)}>
                {copied === 'script' ? t('common.copied') : t('widget.copyCode')}
              </Button>
            </div>
            <p className="field-hint mb-2">
              {settings.mode === 'inline' ? t('widget.embedJsHintInline') : t('widget.embedJsHintFloat')}
            </p>
            <pre className="overflow-x-auto rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-3 text-xs leading-relaxed">
              <code>{scriptSnippet}</code>
            </pre>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="field-label mb-0">{t('widget.embedIframeTitle')}</p>
              <Button onClick={() => void copy('iframe', iframeSnippet)}>
                {copied === 'iframe' ? t('common.copied') : t('widget.copyCode')}
              </Button>
            </div>
            <p className="field-hint mb-2">{t('widget.embedIframeHint')}</p>
            <pre className="overflow-x-auto rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-3 text-xs leading-relaxed">
              <code>{iframeSnippet}</code>
            </pre>
          </div>
        </>
      ) : null}
    </section>
  );
}
